import React, { useEffect, useRef } from "react";

import cn from "classnames";
import type HlsPlayer from "hls.js";

import styles from "./HlsVideo.module.scss";

export interface HlsVideoProps {
  autoPlay?: boolean;
  className?: string;
  // Playback reached the end. Fires on both the native and the hls.js path, since `ended` is
  // an event on the element itself rather than anything the demuxer owns.
  onEnded?: () => void;
  onError?: () => void;
  url: string;
}

// Safari/iOS play HLS (.m3u8) natively; every other browser needs hls.js.
const NATIVE_HLS_MIME = "application/vnd.apple.mpegurl";

// hls.js is only for HLS the browser can't play itself. An mp4 or webm is handed straight to
// the element, which already knows how to play it — so a url does not have to be a stream.
const isHlsSource = (url: string) => /\.m3u8(\?|#|$)/i.test(url);

// A Picture-in-Picture window outlives the <video> it came from: detaching the element leaves
// the PiP window playing, with nothing left in the app to pause it. So stop playback and hand
// the element back from PiP before letting go of it.
const releaseVideo = (video: HTMLVideoElement) => {
  if (document.pictureInPictureElement === video) {
    // Rejects if PiP is already closing; playback is stopped below either way.
    document.exitPictureInPicture().catch(() => undefined);
  }
  video.pause();
  // Drop the source too, or a buffered stream can keep sounding after the element is gone.
  video.removeAttribute("src");
  video.load();
};

const HlsVideo = ({ autoPlay, className, onEnded, onError, url }: HlsVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<HlsPlayer>();

  // Separate from the source effect so it is not re-bound whenever the url changes, and so it
  // covers both playback paths. `releaseVideo`'s load() raises `emptied`, never `ended`, so
  // tearing the player down cannot masquerade as the viewer finishing the video.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onEnded) {
      return;
    }
    const handleEnded = () => onEnded();
    video.addEventListener("ended", handleEnded);
    return () => video.removeEventListener("ended", handleEnded);
  }, [onEnded]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (!isHlsSource(url) || video.canPlayType(NATIVE_HLS_MIME)) {
      const handleNativeError = () => onError?.();
      video.addEventListener("error", handleNativeError);
      video.src = url;
      return () => {
        video.removeEventListener("error", handleNativeError);
        releaseVideo(video);
      };
    }

    // Lazy-load hls.js so it stays out of the main bundle.
    let cancelled = false;
    import("hls.js")
      .then(({ default: Hls }) => {
        if (cancelled) {
          return;
        }
        if (!Hls.isSupported()) {
          onError?.();
          return;
        }
        const player = new Hls();
        hlsRef.current = player;
        player.loadSource(url);
        player.attachMedia(video);
        player.on(Hls.Events.ERROR, (_event, data) => {
          // Teardown detaches the media, which can itself raise a fatal error. Reporting it
          // would latch the runner's error state on and blank the video on its next opening.
          if (data.fatal && !cancelled) {
            onError?.();
          }
        });
      })
      .catch(() => {
        // Chunk failed to load — surface the same fallback as a playback error.
        if (!cancelled) {
          onError?.();
        }
      });

    return () => {
      cancelled = true;
      // Detach hls.js first: releaseVideo() resets the element, which would pull the
      // MediaSource out from under a still-attached player.
      hlsRef.current?.destroy();
      hlsRef.current = undefined;
      releaseVideo(video);
    };
  }, [onError, url]);

  return (
    <video
      autoPlay={autoPlay}
      className={cn(styles.video, className)}
      controls
      // Watching the video *is* the guide, and it is tracked by the popup that owns it, so it
      // must not outlive it. PiP would detach it into a window the app can neither show nor
      // stop, and the PiP window's own transport buttons are browser chrome we don't drive.
      disablePictureInPicture
      playsInline
      ref={videoRef}
    />
  );
};

export default HlsVideo;
